import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GoogleLogin } from "@react-oauth/google";
import { FaTools, FaEye, FaEyeSlash } from "react-icons/fa";
import Logo from "../../components/Logo";
import api from "../../utils/api";
import { validateEmail } from "../../utils/validation";
import { getSocket } from "../../utils/socket";
import { useMaintenance } from "../../context/MaintenanceContext";
import "./Auth.css";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { maintenance } = useMaintenance();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [shake, setShake] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  /** Email (normalized) that passed check-login-email — password step only after this matches current email */
  const [verifiedEmail, setVerifiedEmail] = useState(null);

  const emailNorm = formData.email.trim().toLowerCase();
  const emailLooksRegistered =
    !!verifiedEmail &&
    emailNorm === verifiedEmail &&
    showPasswordField;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    if (name === "email") {
      const next = value.trim().toLowerCase();
      if (verifiedEmail && next !== verifiedEmail) {
        setShowPasswordField(false);
        setVerifiedEmail(null);
        setFormData((prev) => ({ ...prev, password: "" }));
      }
    }
  };

  const checkEmailRegistered = async () => {
    const raw = formData.email.trim();
    const normalized = raw.toLowerCase();
    setErrors((prev) => ({ ...prev, submit: "" }));

    if (!raw) {
      setErrors({ email: t("login.emailRequired") });
      return false;
    }
    if (!validateEmail(raw)) {
      setErrors({ email: t("login.validEmail") });
      setShowPasswordField(false);
      setVerifiedEmail(null);
      setFormData((p) => ({ ...p, password: "" }));
      return false;
    }

    setIsCheckingEmail(true);
    try {
      const { data } = await api.post("/auth/check-login-email", {
        email: normalized,
      });
      if (!data.exists) {
        setShowPasswordField(false);
        setVerifiedEmail(null);
        setFormData((p) => ({ ...p, password: "" }));
        setErrors({ email: t("login.emailNotRegistered") });
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return false;
      }
      if (!data.passwordLoginAllowed) {
        setShowPasswordField(false);
        setVerifiedEmail(null);
        setFormData((p) => ({ ...p, password: "" }));
        setErrors({ submit: t("login.useGoogleSignIn") });
        setShake(true);
        setTimeout(() => setShake(false), 500);
        return false;
      }
      setVerifiedEmail(normalized);
      setShowPasswordField(true);
      setErrors((prev) => ({ ...prev, email: "" }));
      return true;
    } catch (err) {
      const msg =
        err.response?.data?.message || t("login.incorrectCredentials");
      setErrors({ submit: msg });
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!showPasswordField || emailNorm !== verifiedEmail) {
      await checkEmailRegistered();
      return;
    }

    const newErrors = {};
    if (!formData.password) {
      newErrors.password = t("login.passwordRequired");
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const response = await api.post("/auth/login", {
        email: verifiedEmail,
        password: formData.password,
      });

      localStorage.setItem("token", response.data.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: response.data._id,
          username: response.data.username,
          name: response.data.name,
          email: response.data.email,
          role: response.data.role,
          verified: response.data.verified,
          profileImage: response.data.profileImage || null,
          gender: response.data.gender || "",
          dateOfBirth: response.data.dateOfBirth || "",
        }),
      );
      window.dispatchEvent(new Event("userUpdated"));
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      }

      getSocket();

      const role = response.data.role;
      if (role === "admin") {
        navigate("/admin/dashboard");
      } else if (role === "farmer") {
        navigate("/farmer/dashboard");
      } else {
        navigate("/consumer/dashboard");
      }
    } catch (error) {
      setIsLoading(false);
      const msg = error.response?.data?.message || "";
      const ml = String(msg).toLowerCase();
      if (ml.includes("google")) {
        setErrors({ submit: msg });
      } else if (ml.includes("invalid email")) {
        setErrors({ email: t("login.emailNotRegistered") });
        setShowPasswordField(false);
        setVerifiedEmail(null);
        setFormData((p) => ({ ...p, password: "" }));
      } else if (
        ml.includes("incorrect password") ||
        ml.includes("wrong password")
      ) {
        setErrors({ password: t("login.wrongPassword") });
      } else {
        setErrors({ submit: msg || t("login.incorrectCredentials") });
      }
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) return;
    setIsLoading(true);
    setErrors({});
    try {
      const response = await api.post("/auth/google", {
        credential: credentialResponse.credential,
      });
      localStorage.setItem("token", response.data.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: response.data._id,
          username: response.data.username,
          name: response.data.name,
          email: response.data.email,
          role: response.data.role,
          verified: response.data.verified,
          profileImage: response.data.profileImage || null,
          gender: response.data.gender || "",
          dateOfBirth: response.data.dateOfBirth || "",
        }),
      );
      window.dispatchEvent(new Event("userUpdated"));
      getSocket();
      const role = response.data.role;
      if (role === "admin") navigate("/admin/dashboard");
      else if (role === "farmer") navigate("/farmer/dashboard");
      else navigate("/consumer/dashboard");
    } catch (error) {
      setIsLoading(false);
      const errMsg = error.response?.data?.message || t("login.googleFailed");
      setErrors({ submit: errMsg });
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const handleGoogleError = () => {
    setErrors({ submit: t("login.googleCancelled") });
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const googleClientId =
    process.env.REACT_APP_GOOGLE_CLIENT_ID ||
    "527730472160-0up0itis0lbgddvdjtciptvh8c7b6nl6.apps.googleusercontent.com";

  const busy = isLoading || isCheckingEmail;
  const primaryLabel = emailLooksRegistered
    ? t("common.login")
    : t("login.continue");

  return (
    <div className="auth-container">
      <div className={`auth-card ${shake ? "shake" : ""}`}>
        <Link to="/">
          <Logo />
        </Link>

        <div className="auth-card-inner">
          <h3 className="auth-card-title">{t("login.title")}</h3>

          {maintenance && (
            <div className="auth-maintenance-banner">
              <FaTools className="auth-maintenance-banner-icon" />
              <div>
                <strong>{t("maintenance.title", "Under Maintenance")}</strong>
                <p>
                  {t(
                    "maintenance.loginNotice",
                    "The site is currently under maintenance. Only administrators can access the system.",
                  )}
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleFormSubmit} className="auth-form">
            {errors.submit && (
              <div className="auth-error-message">{errors.submit}</div>
            )}

            <div className="auth-form-group">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`auth-form-input ${errors.email ? "error" : ""} ${emailLooksRegistered ? "validated" : ""}`}
                placeholder={t("login.enterEmail")}
                autoComplete="email"
              />
              {errors.email && (
                <span className="auth-field-error">{errors.email}</span>
              )}
            </div>

            <div
              className={`auth-form-group ${showPasswordField ? "auth-field-revealed" : "auth-field-hidden"}`}
            >
              <label htmlFor="password">{t("login.password")}</label>
              <div className="auth-password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`auth-form-input ${errors.password ? "error" : ""}`}
                  placeholder={t("login.enterPassword")}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={
                    showPassword
                      ? t("login.hidePassword")
                      : t("login.showPassword")
                  }
                >
                  {showPassword ? (
                    <FaEyeSlash aria-hidden />
                  ) : (
                    <FaEye aria-hidden />
                  )}
                </button>
              </div>
              {errors.password && (
                <span className="auth-field-error">{errors.password}</span>
              )}
            </div>

            {emailLooksRegistered && (
              <div className="auth-form-options">
                <label className="auth-checkbox-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>{t("login.rememberMe")}</span>
                </label>
                <Link to="/forgot-password" className="auth-forgot-password-link">
                  {t("login.forgotPassword")}
                </Link>
              </div>
            )}

            <button type="submit" className="auth-btn-primary" disabled={busy}>
              {isCheckingEmail
                ? t("login.checkingEmail")
                : isLoading
                  ? t("login.loggingIn")
                  : primaryLabel}
            </button>
          </form>

          {/* Google sign-in */}
          {googleClientId && (
            <div className="auth-social-login-section">
              <div className="auth-divider">
                <span>{t("common.or")}</span>
              </div>
              <div className="auth-google-wrapper">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="filled_blue"
                  size="large"
                  text="continue_with"
                  shape="rectangular"
                  width="320"
                />
              </div>
            </div>
          )}
          {!googleClientId && (
            <div className="auth-social-login-section">
              <div className="auth-divider">
                <span>{t("common.or")}</span>
              </div>
              <p className="auth-google-disabled">
                {t("login.googleNotConfigured")}
              </p>
            </div>
          )}

          <div className="auth-footer">
            <span>{t("login.noAccount")} </span>
            <Link to="/signup" className="auth-link">
              {t("login.signUp")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
